"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CalendarClock, ChevronLeft, Timer, Trophy, User, Users } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { JudgeDarkFrame } from "@/features/judge/components/shell/JudgeDarkHead";
import {
  contestHostLine,
  contestZoneLabel,
  formatDateTimeWithoutZone,
  formatDuration,
} from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import { roundHref } from "../lib/round";
import { contestAccent, contestAccentStyle } from "../lib/contest-look";
import type { ContestAuthorCredit, PublicContestDetail } from "../types";
import { AuthorHandle } from "./AuthorHandle";
import { DivisionLine, DivisionTag } from "./DivisionTag";
import { SponsorStrip } from "./SponsorStrip";

/** `EX-CPG02`: how many authors the byline names before "and N more". */
const BYLINE_AUTHORS = 3;

/**
 * `EX-CPG02`, Wave 30 decision 4: who wrote the round, under its title, the
 * way Codeforces prints its writers: "by" and each handle in its rating band's
 * colour, a lead first (the server's order). Nothing when nobody is credited.
 * Shared with the directory card, where `total` is the whole list's length.
 */
export function ContestByline({
  authors,
  total,
  locale,
  tone,
  className,
}: {
  authors: ReadonlyArray<ContestAuthorCredit>;
  total: number;
  locale: string;
  tone: "dark" | "light";
  className?: string;
}) {
  const t = useTranslations("contest.page");
  if (authors.length === 0) return null;
  const shown = authors.slice(0, BYLINE_AUTHORS);
  const more = Math.max(0, total - shown.length);
  return (
    <p data-testid="contest-byline" className={cn("flex flex-wrap items-baseline gap-x-1.5 gap-y-1", className)}>
      {/* Real spaces between the parts (the flex gap is only visual), so it reads "by a, b and 2 more". */}
      <span>{t("byline")}</span>{" "}
      {shown.map((author, index) => (
        <span key={author.username ?? author.name} className="inline-flex items-baseline">
          <AuthorHandle person={author} locale={locale} tone={tone} />
          {index < shown.length - 1 ? "," : null}
          {index < shown.length - 1 || more > 0 ? " " : null}
        </span>
      ))}
      {more > 0 ? <span>{t("bylineMore", { count: formatNumber(more, locale) })}</span> : null}
    </p>
  );
}

const BADGE_TONE = {
  plain: "border-white/15 text-neutral-300",
  brand: "border-primary/45 bg-primary/10 text-accent-peach",
  live: "border-primary/55 bg-primary/15 text-accent-peach",
  rated: "border-violet-400/40 bg-violet-400/10 text-violet-200",
  frozen: "border-sky-400/40 bg-sky-400/10 text-sky-200",
  final: "border-emerald-400/35 bg-emerald-400/10 text-emerald-300",
} as const;

type Badge = { label: string; tone: keyof typeof BADGE_TONE; icon?: ReactNode; dot?: boolean };

/**
 * `EX-HST03`: the host organization, the person pill's shape with the logo
 * drawn whole on white (a logo is never cropped to a circle) and no link.
 */
function HostOrganization({ name, logoImageId }: { name: string; logoImageId: string | null }) {
  return (
    <span
      data-testid="contest-host-organization"
      className="inline-flex h-8 items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-3 text-sm"
    >
      {logoImageId ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={cfImageUrl(logoImageId, "avatar")} alt="" className="h-6 w-6 rounded-full bg-white object-contain p-0.5" />
      ) : (
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold uppercase text-white"
        >
          {name.charAt(0)}
        </span>
      )}
      <span className="font-medium text-white">{name}</span>
    </span>
  );
}

/**
 * One person the head credits, as the leaderboard's identity rule allows: a
 * name, a handle and an avatar, never a user id, linked when there is a handle.
 */
function Person({
  name,
  username,
  avatarImageId,
  locale,
  lead,
}: {
  name: string;
  username: string | null;
  avatarImageId: string | null;
  locale: string;
  lead?: string;
}) {
  const body = (
    <>
      {avatarImageId ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={cfImageUrl(avatarImageId, "avatar")} alt="" className="h-6 w-6 rounded-full object-cover" />
      ) : (
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold uppercase text-white"
        >
          {name.charAt(0)}
        </span>
      )}
      <span className="font-medium text-white">{name}</span>
      {lead ? (
        <span className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-accent-peach">
          {lead}
        </span>
      ) : null}
    </>
  );
  const className =
    "inline-flex h-8 items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-3 text-sm transition-colors duration-200";
  return username ? (
    <Link href={`/${locale}/u/${username}`} className={cn(className, "hover:border-primary/60")}>
      {body}
    </Link>
  ) : (
    <span className={className}>{body}</span>
  );
}

/**
 * The head of `/contests/[slug]`, `EX-PUB22` (the "Judge pages" proposal's
 * Contest board): whose contest it is and what state it is in, on the Judge's
 * dark ground, with the door beside it.
 *
 * Laid out on its own grid rather than through `JudgeDarkHead`, because a
 * phone reads the door right after the title. From `lg` the door sits on the
 * right.
 *
 * `EX-CPG02` (Wave 30): the description and the credits moved to the
 * Overview tab; the authors are named under the title instead, the freeze and
 * the AI policy moved to the Overview's details, and the page's tabs sit on
 * the head's bottom edge (`tabs`, a `HeadTabs`).
 *
 * `EX-SPN01` (`docs/29` §6.23): on a contest whose stamp carries them, the
 * organizer's cover sits behind the head under a dark scrim, their accent
 * colour takes the door's primary actions (`Button variant="accent"`, held to
 * 4.5:1 for white words by the quiz's guard), the head's icons and a line
 * along its top edge (3:1 on the near-black), and the sponsors close the head
 * when the contest page's switch is on. The Judge bar above and the tabs stay
 * Progsity's.
 */
export function ContestHead({
  contest,
  locale,
  door,
  tabs,
}: {
  contest: PublicContestDetail;
  locale: string;
  door: ReactNode;
  tabs?: ReactNode;
}) {
  const t = useTranslations("contest.page");
  const tCommon = useTranslations("contest");
  const tRound = useTranslations("contest.round");
  const zone = useContestDisplayZone();

  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const hostLine = contestHostLine(contest, locale);
  const duration = formatDuration(contest.startAt, contest.endAt, locale);
  const hasEnded = contest.phase === "ended" || contest.phase === "unfrozen";
  const people = formatNumber(contest.participantCount, locale);
  const brand = contest.brand ?? null;
  const accent = contestAccent(brand?.accentColor);
  const cover = brand?.coverImageId ? cfImageUrl(brand.coverImageId, "hero") : "";
  const sponsors = brand?.show.contestPage ? brand.sponsors : [];

  // `docs/29` §6: the clock decides the phase and this only paints it. A board
  // left frozen after the end is not final yet, and says so.
  const phaseBadges: Badge[] = (() => {
    switch (contest.phase) {
      case "running":
        return [{ label: t("badgeLive"), tone: "live", dot: true }];
      case "frozen":
        return [
          { label: t("badgeLive"), tone: "live", dot: true },
          { label: t("badgeBoardFrozen"), tone: "frozen" },
        ];
      case "ended":
        return contest.freezeAt !== null
          ? [
              { label: tCommon("phase.ended"), tone: "plain" },
              { label: t("badgeBoardFrozen"), tone: "frozen" },
            ]
          : [{ label: t("badgeFinal"), tone: "final" }];
      case "unfrozen":
        return [{ label: t("badgeFinal"), tone: "final" }];
      default:
        return [{ label: tCommon(`phase.${contest.phase}`), tone: "plain" }];
    }
  })();

  const badges: Badge[] = [
    // `EX-ADM03`, `docs/29` §9: who runs it, and (`docs/29` §7.1) whether a
    // rating is at risk, both before a contestant decides to compete.
    contest.official
      ? { label: t("badgeOfficial"), tone: "brand", icon: <Trophy className="h-3.5 w-3.5" aria-hidden /> }
      : { label: tCommon("community"), tone: "plain" },
    contest.rated ? { label: tCommon("rated"), tone: "rated" } : { label: t("badgeUnrated"), tone: "plain" },
    ...phaseBadges,
  ];

  const meta: Array<{ key: string; icon: ReactNode; text: ReactNode }> = [
    {
      key: "when",
      icon: <CalendarClock className="h-4 w-4" aria-hidden />,
      text: contest.startAt ? (
        <>
          <b className="font-medium text-white">{formatDateTimeWithoutZone(contest.startAt, locale, zone)}</b>{" "}
          {contestZoneLabel(locale, zone)}
        </>
      ) : (
        "-"
      ),
    },
    ...(duration ? [{ key: "length", icon: <Timer className="h-4 w-4" aria-hidden />, text: duration }] : []),
    { key: "format", icon: <Trophy className="h-4 w-4" aria-hidden />, text: tCommon(`scoring.${contest.scoringFormat}`) },
    {
      key: "who",
      icon: <User className="h-4 w-4" aria-hidden />,
      text: tCommon(`participation.${contest.participation}`),
    },
    {
      key: "people",
      icon: <Users className="h-4 w-4" aria-hidden />,
      text: (
        <b className="font-medium text-white">
          {hasEnded
            ? t("participantCountEnded", { count: people })
            : contest.isOpen
              ? t("participantCountLive", { count: people })
              : t("participantCount", { count: people })}
        </b>
      ),
    },
  ];

  return (
    <JudgeDarkFrame labelledBy="contest-title">
      {cover ? (
        <div aria-hidden data-testid="contest-cover" className="pointer-events-none absolute inset-0 -z-[1]">
          {/* eslint-disable-next-line @next/next/no-img-element -- a Cloudflare Images delivery URL, already sized */}
          <img src={cover} alt="" className="h-full w-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/55" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-neutral-950 to-transparent" />
        </div>
      ) : null}
      {accent ? (
        <div aria-hidden data-testid="contest-accent-bar" className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent.onDark }} />
      ) : null}
      <div className={cn("grid gap-y-6 pt-7 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-x-12", tabs ? "pb-7 sm:pb-8" : "pb-10 sm:pb-11")}>
        <div className="min-w-0">
          <Link
            href={`/${locale}/contests`}
            className="inline-flex items-center gap-1.5 text-[13px] text-neutral-400 transition-colors duration-200 hover:text-accent-peach"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            {t("backToDirectory")}
          </Link>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {badges.map((badge) => (
              <span
                key={badge.label}
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12.5px] font-medium",
                  BADGE_TONE[badge.tone],
                )}
              >
                {badge.dot ? (
                  <span className="h-2 w-2 rounded-full bg-primary ring-[3px] ring-primary/25" aria-hidden />
                ) : null}
                {badge.icon}
                {badge.label}
              </span>
            ))}
            {/* `EX-DIV03`: the division and who it rates, beside Rated. */}
            {contest.division ? (
              <>
                <DivisionTag division={contest.division} locale={locale} tone="dark" />
                <DivisionLine division={contest.division} locale={locale} tone="dark" className="text-[13px] text-neutral-300" />
              </>
            ) : null}
          </div>

          {/*
            `EX-DIV06`, `docs/29` §8.1: a division of a round says so, and the
            round page is where a reader's rating picks their division.
          */}
          {contest.round ? (
            <p data-testid="contest-round-line" className="mt-3 text-sm leading-relaxed text-neutral-300">
              {tRound.rich("partOf", {
                link: (chunks) => (
                  <Link
                    href={roundHref(locale, contest.round!.slug)}
                    className="font-semibold text-accent-peach underline underline-offset-2 transition-colors duration-200 hover:text-white"
                  >
                    {chunks}
                  </Link>
                ),
                label: (contest.division?.label ?? "").replace(/ /g, "\u00a0"),
                title: (locale === "bn" ? contest.round.title_bn : null) ?? contest.round.title_en,
                others: contest.round.divisions
                  .filter((division) => division.slug !== contest.slug)
                  .map((division) => (division.label ?? "").replace(/ /g, "\u00a0"))
                  .join(tRound("partOfJoin")),
              })}
            </p>
          ) : null}

          <h1
            id="contest-title"
            className="mt-4 text-3xl font-bold leading-tight tracking-tight sm:text-[44px] sm:leading-[1.08]"
          >
            {title}
          </h1>

          {/* `EX-CPG02`, Wave 30 decision 4: who wrote it, under the title. */}
          <ContestByline
            authors={contest.authors}
            total={contest.authors.length}
            locale={locale}
            tone="dark"
            className="mt-3.5 text-[14.5px] text-neutral-300"
          />

          {/* `EX-PUB19`: the host of a community contest; a Progsity round answers `host: null`.
              `EX-HST03`: an organization in its place, or beside Progsity on a Progsity round. */}
          {hostLine ? (
            <p className="mt-4 flex flex-wrap items-center gap-2.5 text-sm text-neutral-300">
              {t("creditsHost")}
              {hostLine.kind === "organization" ? (
                <HostOrganization name={hostLine.name} logoImageId={hostLine.logoImageId} />
              ) : (
                <Person
                  name={hostLine.name}
                  username={hostLine.person.username}
                  avatarImageId={hostLine.person.avatarImageId}
                  locale={locale}
                />
              )}
            </p>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-x-[22px] gap-y-2.5 text-sm text-neutral-300">
            {meta.map((item) => (
              <span key={item.key} className="inline-flex items-center gap-2">
                <span className="text-primary" style={accent ? { color: accent.onDark } : undefined}>
                  {item.icon}
                </span>
                <span>{item.text}</span>
              </span>
            ))}
          </div>

          {sponsors.length ? (
            <SponsorStrip sponsors={sponsors} tone="dark" className="mt-6 border-t border-white/10 pt-5" />
          ) : null}
        </div>

        <div className="min-w-0 lg:col-start-2 lg:row-start-1" style={contestAccentStyle(brand?.accentColor)} data-testid="contest-door">
          {door}
        </div>
      </div>

      {/* `EX-CPG02`, Wave 30 decision 5: the page's sections on the head's bottom edge. */}
      {tabs}
    </JudgeDarkFrame>
  );
}
